const firebaseConfig = {
  apiKey: "AIzaSyCh_v0Oe3tCAtVGvuW3spWQMP9PD97WBpo",
  authDomain: "divedix-421ab.firebaseapp.com",
  databaseURL: "https://divedix-421ab-default-rtdb.firebaseio.com",
  projectId: "divedix-421ab",
  storageBucket: "divedix-421ab.firebasestorage.app",
  messagingSenderId: "330094689219",
  appId: "1:330094689219:web:38b30bddfe0239d978db76",
};

let firebaseAppPromise;
let appCheckPromise;
let authContextPromise;

const databaseUrl = (path, query = "") => {
  const encodedPath = String(path)
    .split("/")
    .filter(Boolean)
    .map(encodeURIComponent)
    .join("/");
  return `${firebaseConfig.databaseURL}/${encodedPath}.json${query}`;
};

const requestDatabase = async (path, {
  method = "GET",
  value,
  authToken = "",
  keepalive = false,
} = {}) => {
  const params = new URLSearchParams();
  if (authToken) params.set("auth", authToken);
  if (method !== "GET") params.set("print", "silent");
  const query = params.size ? `?${params}` : "";
  const response = await fetch(databaseUrl(path, query), {
    method,
    headers: value === undefined
      ? { Accept: "application/json" }
      : { Accept: "application/json", "Content-Type": "application/json" },
    body: value === undefined ? undefined : JSON.stringify(value),
    cache: method === "GET" ? "no-store" : undefined,
    keepalive,
  });
  if (!response.ok) throw new Error(`Firebase REST respondió ${response.status}.`);
  if (method !== "GET" || response.status === 204) return null;
  return response.json();
};

const getFirebaseApp = () => {
  if (!firebaseAppPromise) {
    firebaseAppPromise = import("firebase/app").then(({ getApps, initializeApp }) => (
      getApps()[0] || initializeApp(firebaseConfig)
    ));
  }
  return firebaseAppPromise;
};

const ensureAppCheck = () => {
  if (!appCheckPromise) {
    appCheckPromise = Promise.all([getFirebaseApp(), import("firebase/app-check")])
      .then(([app, { initializeAppCheck, ReCaptchaEnterpriseProvider }]) => initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider("6LdFIlAtAAAAAAxTaMXeiYYhrzyuK5LcO-fs6itX"),
        isTokenAutoRefreshEnabled: true,
      }))
      .catch(() => null);
  }
  return appCheckPromise;
};

const getAuthContext = () => {
  if (!authContextPromise) {
    authContextPromise = Promise.all([getFirebaseApp(), ensureAppCheck()])
      .then(async ([app]) => {
        const sdk = await import("firebase/auth");
        return { auth: sdk.getAuth(app), sdk };
      });
  }
  return authContextPromise;
};

export async function fetchRemoteCatalog() {
  return requestDatabase("catalogo");
}

export async function saveRemoteCatalog(catalog) {
  return requestDatabase("catalogo", { method: "PUT", value: catalog });
}

export function subscribeToComments(onComments, onError) {
  let active = true;
  let timer;
  const refresh = async () => {
    window.clearTimeout(timer);
    try {
      const comments = await requestDatabase("comentarios");
      if (!active) return;
      const rows = Object.entries(comments || {})
        .map(([key, value]) => ({ key, ...value }))
        .sort((left, right) => Number(right.timestamp || 0) - Number(left.timestamp || 0))
        .slice(0, 30);
      onComments(rows);
    } catch (error) {
      if (active) onError?.(error);
    } finally {
      if (active) timer = window.setTimeout(refresh, 30000);
    }
  };
  window.addEventListener("divedix-comments-updated", refresh);
  refresh();
  return () => {
    active = false;
    window.clearTimeout(timer);
    window.removeEventListener("divedix-comments-updated", refresh);
  };
}

export function subscribeToUser(callback) {
  let active = true;
  let unsubscribe = () => undefined;
  getAuthContext().then(({ auth, sdk }) => {
    if (active) unsubscribe = sdk.onAuthStateChanged(auth, callback);
  }).catch(() => callback(null));
  return () => {
    active = false;
    unsubscribe();
  };
}

export async function loginWithGoogle() {
  const { auth, sdk } = await getAuthContext();
  return sdk.signInWithPopup(auth, new sdk.GoogleAuthProvider());
}

export async function loginWithEmail(email, password) {
  const { auth, sdk } = await getAuthContext();
  return sdk.signInWithEmailAndPassword(auth, email, password);
}

export async function registerWithEmail(email, password) {
  const { auth, sdk } = await getAuthContext();
  const credential = await sdk.createUserWithEmailAndPassword(auth, email, password);
  await sdk.updateProfile(credential.user, { displayName: email.split("@")[0] });
  return credential;
}

export async function requestPasswordReset(email) {
  const { auth, sdk } = await getAuthContext();
  return sdk.sendPasswordResetEmail(auth, email);
}

export async function logout() {
  const { auth, sdk } = await getAuthContext();
  return sdk.signOut(auth);
}

export async function publishComment(user, text, publicName = "") {
  if (!user?.uid) throw new Error("Debes iniciar sesión para publicar una opinión.");
  const authToken = await user.getIdToken();
  const name = publicName || user?.displayName || user?.email?.split("@")[0] || "Cliente";
  const result = await requestDatabase("comentarios", { method: "POST", authToken, value: {
    id: `com-${Date.now()}`,
    uid: user.uid,
    nombre: name,
    texto: text,
    foto: user?.photoURL || "",
    timestamp: Date.now(),
  } });
  window.dispatchEvent(new Event("divedix-comments-updated"));
  return result;
}

export async function deleteComment(key) {
  const { auth } = await getAuthContext();
  if (!auth.currentUser) throw new Error("Debes iniciar sesión para eliminar una opinión.");
  const authToken = await auth.currentUser.getIdToken();
  return requestDatabase(`comentarios/${key}`, { method: "DELETE", authToken });
}

const analyticsDate = () => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Santo_Domingo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date()).map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
};

export async function trackMetric(path) {
  return requestDatabase(`analytics/${analyticsDate()}/${path}`, {
    method: "PUT",
    value: { ".sv": { increment: 1 } },
    keepalive: true,
  }).catch(() => undefined);
}

const VISITOR_KEY = "divedix-unique-visitor";

const getVisitorId = () => {
  let visitorId = localStorage.getItem(VISITOR_KEY);
  if (!visitorId) {
    visitorId = globalThis.crypto?.randomUUID?.()
      || `visitor-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(VISITOR_KEY, visitorId);
  }
  return visitorId;
};

export async function trackUniqueVisit(device) {
  const visitorId = getVisitorId();
  return requestDatabase(`analytics/${analyticsDate()}/visitors/${visitorId}`, { method: "PUT", keepalive: true, value: {
    device,
    firstSeen: Date.now(),
  } }).catch(() => undefined);
}

export async function trackUniqueProductView(productId) {
  const visitorId = getVisitorId();
  const safeProductId = String(productId)
    .replaceAll(".", "-")
    .replaceAll("#", "-")
    .replaceAll("$", "-")
    .replaceAll("[", "-")
    .replaceAll("]", "-")
    .replaceAll("/", "-");
  return requestDatabase(`analytics/${analyticsDate()}/productVisitors/${safeProductId}/${visitorId}`, {
    method: "PUT",
    value: true,
    keepalive: true,
  }).catch(() => undefined);
}

export async function fetchAnalytics(date) {
  return (await requestDatabase(`analytics/${date}`)) || {};
}
